import {
  IAcceptPartProTradeBid,
  IPartProTradeBid,
  IPartProTradeBidVehicleDetail,
} from "../models/partPro/IPartProTradeBids";

/** Same key the pricing matrix uses for its cells. */
export const matrixCellKey = (partCategoryId: string, accountId: string) =>
  `${partCategoryId}-${accountId}`;

export interface IPendingMatrixBids {
  /** Bids from an account on a category it prices, keyed by matrix cell - highest first */
  byCell: Record<string, IPartProTradeBid[]>;
  /**
   * Bids on a matrix row with no cell of their own: a buyer with no Core account, or an account
   * not set up for that category. Keyed by part category id.
   */
  otherByCategory: Record<string, IPartProTradeBid[]>;
  /** The category (PartPro lot) each waiting bid is on */
  lotIdByBidId: Record<string, string>;
  /** Every bid still waiting for a decision, including categories the matrix doesn't show */
  bidCount: number;
  categoryCount: number;
}

/**
 * Lays a vehicle's PartPro bids over its pricing matrix. Only undecided categories count: an
 * accepted bid is already the cell's price, and a passed one is over.
 */
export function groupPendingMatrixBids(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  enabledCellKeys: Set<string>
): IPendingMatrixBids {
  const result: IPendingMatrixBids = { byCell: {}, otherByCategory: {}, lotIdByBidId: {}, bidCount: 0, categoryCount: 0 };

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0)
    .forEach((lot) => {
      result.bidCount += lot.bids.length;
      result.categoryCount += 1;
      lot.bids.forEach((bid) => (result.lotIdByBidId[bid.id] = lot.id));
      if (!lot.partCategoryId) return;
      const categoryId = lot.partCategoryId;

      lot.bids.forEach((bid) => {
        const key = bid.accountId ? matrixCellKey(categoryId, bid.accountId) : null;
        if (key && enabledCellKeys.has(key)) (result.byCell[key] ??= []).push(bid);
        else (result.otherByCategory[categoryId] ??= []).push(bid);
      });
    });

  return result;
}

/** Bids picked to accept, keyed by lot - PartPro awards one bid per category. */
export type BidPicks = Record<string, string>;

/** Pick a bid, replacing any other pick on its category; picking it again un-picks it. */
export function toggleBidPick(picks: BidPicks, lotId: string, bidId: string): BidPicks {
  const next = { ...picks };
  if (next[lotId] === bidId) delete next[lotId];
  else next[lotId] = bidId;
  return next;
}

/** Drops picks whose bid was withdrawn or whose category was decided since they were made. */
export function livePicks(picks: BidPicks, pending: IPendingMatrixBids): BidPicks {
  return Object.fromEntries(
    Object.entries(picks).filter(([lotId, bidId]) => pending.lotIdByBidId[bidId] === lotId)
  );
}

export const picksToAccept = (picks: BidPicks): IAcceptPartProTradeBid[] =>
  Object.entries(picks).map(([lotId, bidId]) => ({ lotId, bidId }));

/** Who a bid is from, preferring the Core account name. */
export const bidderName = (bid: IPartProTradeBid) => bid.accountName ?? bid.buyerCompanyName;
